/**
 * Soils labels: each test's label with its leader to the test's dot — drawn the way the concrete
 * callouts' leaders are, and behaving as a concrete callout does:
 *   • a leader leaves its label at the centre of an edge — a test's badge and a shot's tag by their
 *     left or right side alone (2026-10-24, Ricky: never from the top or bottom), whichever faces the
 *     dot best — by a short stub straight out of it, then runs to the dot, never turning back
 *   • it never runs under another label, a callout, a note or a test's dot, and crosses another
 *     leader only when the way round is much longer: it takes the shortest way round them (route.ts)
 *   • a label rests only on the plan, clear of every other label and box and of every test's dot;
 *     carried where it may not rest, it settles on the nearest place it may (`nearestWhere`,
 *     callout.ts)
 * (The shared "Work area(s) observed" and "Failing area(s)" labels are laid out and routed as
 * callouts are: labels.ts.)
 *
 * Pure, in image pixels.
 */
import type { Vec } from '../../shared/doc.ts';
import { dist, rectsOverlap, type Rect } from './geom.ts';
import { easedRadii } from './callout.ts';
import { findPath, lineObstacle, pathClear, polyObstacle, rectObstacle, type Obstacle } from './route.ts';

export { nearestWhere } from './callout.ts';

export interface SoilsLeader {
  /** From the label's edge to the tip (tip last). */
  pts: Vec[];
  tip: Vec;
  /** Unit direction of the last stretch (an arrowhead's). */
  dir: Vec;
  /** How far each corner is eased into an arc (0 at both ends). */
  radii: number[];
  /** Nothing in its way (false: squeezed, it crosses something rather than not be drawn). */
  clear: boolean;
  /** What it leads to: a test's dot (its centre — the line is drawn into the dot, under it, so the dot's ring opens for it), or an area's edge. */
  target: Vec;
  /** A shared label's arrow: the area it points at. */
  of?: string;
  /** A shared label's arrow put on an end of its area. */
  face?: 'left' | 'right' | null;
}

export interface SoilsLeaderOpts {
  /** The label's corner radius. */
  radius: number;
  /** The stub straight out of the label's edge before the leader turns to its target. */
  landing: number;
  /** How far short of the target the tip stops (a dot's ring); 0 for an arrow onto an area. */
  tipInset: number;
  /** A corner's arc. */
  ease: number;
  /** The straight run the tip needs before it (an arrowhead's length); 0 for a dot. */
  finish: number;
  /** Only out of the centre of a side, left or right (an elevation tag's leader), never its top or bottom. */
  sides?: 'lr';
}

/** What a leader keeps clear of: boxes and dots (never under them) and leaders already drawn (not crossed, if there is any other way). */
export interface SoilsAround {
  boxes: readonly Rect[];
  /** Tests' dots (round: a leader may pass as near a dot all round as beside it). */
  dots?: readonly { p: Vec; r: number }[];
  lines: readonly (readonly Vec[])[];
  /** How near a leader may pass a box or a dot, and how far off their corners it goes round them. */
  clear: number;
  reach: number;
}

/** One way out of a label: the centre of an edge and the stub straight out of it, and what taking it costs beyond its length. */
interface Exit {
  head: Vec[];
  /** The way out of the edge it leaves by. */
  out: Vec;
  /** How far the leader must turn from the stub to reach the target (0: straight on, 1: square off it), costed by its length. */
  bias: number;
}

/**
 * The ways out of a label towards a target: the centre of each edge with its stub, cheapest first.
 * A way that has the target ahead of it costs its length and more the more the leader must turn
 * off the stub (so a dot below the label is reached from its bottom, one off to the side from that
 * side); one with the target behind it comes last (it can only go round its own label).
 */
export function exits(rect: Rect, target: Vec, o: Pick<SoilsLeaderOpts, 'landing' | 'sides'>): Exit[] {
  const cx = rect.x + rect.w / 2,
    cy = rect.y + rect.h / 2;
  const s = o.landing;
  const all: [Vec, Vec][] = [
    [{ x: cx, y: rect.y }, { x: 0, y: -1 }],
    [{ x: cx, y: rect.y + rect.h }, { x: 0, y: 1 }],
    [{ x: rect.x, y: cy }, { x: -1, y: 0 }],
    [{ x: rect.x + rect.w, y: cy }, { x: 1, y: 0 }],
  ];
  // (an elevation tag's leader leaves by a side alone)
  const edges = o.sides === 'lr' ? all.slice(2) : all;
  const out = edges.map(([at, n]): Exit & { cost: number } => {
    const p1 = { x: at.x + n.x * s, y: at.y + n.y * s };
    const len = dist(p1, target);
    const cos = len < 1e-6 ? 1 : ((target.x - p1.x) * n.x + (target.y - p1.y) * n.y) / len;
    const bias = cos > 0.05 ? 0.8 * len * (1 - cos) : 1e4 + len;
    return { head: [at, p1], out: n, bias, cost: s + len + bias };
  });
  return out.sort((a, b) => a.cost - b.cost).map(({ head, out, bias }) => ({ head, out, bias }));
}

/** A test's dot as an obstacle: the octagon round it (the dot within it), kept clear by `clear`. */
function dotObstacle(c: Vec, r: number, clear: number, reach: number): Obstacle {
  const R = r / Math.cos(Math.PI / 8);
  const pts: number[] = [];
  for (let k = 0; k < 8; k++) pts.push(c.x + R * Math.cos(Math.PI / 8 + (k * Math.PI) / 4), c.y + R * Math.sin(Math.PI / 8 + (k * Math.PI) / 4));
  return polyObstacle(pts, { x: c.x - R, y: c.y - R, w: 2 * R, h: 2 * R }, clear, reach, 0);
}

/** Whether a leader turns back on itself (a corner sharper than 120°) or comes back behind the edge it left by (`out`, its way out of it). */
function turnsBack(pts: readonly Vec[], out: Vec, landing: number): boolean {
  const s = pts[0]!;
  for (let i = 1; i < pts.length; i++) if ((pts[i]!.x - s.x) * out.x + (pts[i]!.y - s.y) * out.y < -0.1 * landing) return true;
  for (let i = 1; i + 1 < pts.length; i++) {
    const a = unit(pts[i - 1]!, pts[i]!),
      b = unit(pts[i]!, pts[i + 1]!);
    if (dist(pts[i - 1]!, pts[i]!) > 0.5 && dist(pts[i]!, pts[i + 1]!) > 0.5 && a.x * b.x + a.y * b.y < -0.5) return true;
  }
  return false;
}

function unit(a: Vec, b: Vec): Vec {
  const l = dist(a, b) || 1;
  return { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
}
function length(pts: readonly Vec[]): number {
  let l = 0;
  for (let i = 0; i + 1 < pts.length; i++) l += dist(pts[i]!, pts[i + 1]!);
  return l;
}

/**
 * The leader from a label to its target: the plain one (out of the centre of the edge facing it,
 * a stub, then straight) when nothing is in its way; else the shortest way round what is (out of
 * another edge's centre if that is shorter); crossing another leader rather than wander far round
 * it (a crossing costs as much as a good way round); and failing any way, the plain one, saying it
 * is not clear.
 */
export function routeSoilsLeader(rect: Rect, target: Vec, o: SoilsLeaderOpts, around: SoilsAround, bounds: Rect): SoilsLeader {
  // only what lies near the way: an obstacle well off the box round the label and its target can
  // neither be in the way nor be worth going round (on a map crowded with tests, the few near the
  // leader are what matter — searching round every one of them would take seconds)
  const span = dist({ x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 }, target);
  const pad = 6 * o.landing + 0.35 * span;
  const x0 = Math.min(rect.x, target.x) - pad,
    y0 = Math.min(rect.y, target.y) - pad;
  const corridor: Rect = { x: x0, y: y0, w: Math.max(rect.x + rect.w, target.x) + pad - x0, h: Math.max(rect.y + rect.h, target.y) + pad - y0 };
  const near = (ob: Obstacle) => rectsOverlap(ob.bounds, corridor);
  const boxes = [...around.boxes.map((r) => rectObstacle(r, around.clear, around.reach)), ...(around.dots ?? []).map((d) => dotObstacle(d.p, d.r, around.clear, around.reach))].filter(near);
  const lines = around.lines.map(lineObstacle).filter(near);
  // after it leaves, a leader never comes back across its own label
  const own = rectObstacle(rect, Math.min(around.clear, 0.5 * o.landing), around.reach);
  const finish = (pts: Vec[]): SoilsLeader | null => {
    const last = pts[pts.length - 2]!;
    const dir = unit(last, target);
    if (dist(last, target) <= o.tipInset + 0.5) return null;
    const tip = { x: target.x - dir.x * o.tipInset, y: target.y - dir.y * o.tipInset };
    const out = [...pts.slice(0, -1), tip];
    return { pts: out, tip, dir, radii: [], clear: true, target };
  };
  const plans = exits(rect, target, o);
  // crossing another leader (an arrow, say) costs as much as a good way round: a leader goes round when
  // that is not much longer, and crosses rather than wander far off round it
  const crossing = 8 * o.landing;
  /** What a way out here costs at the very least: its stub, then straight to the target, and its turn. */
  const atLeast = ({ head, bias }: Exit) => o.landing + dist(head[head.length - 1]!, target) + bias;
  let best: { pts: Vec[]; cost: number } | null = null;
  for (const soft of lines.length ? [lines, [] as Obstacle[]] : [[] as Obstacle[]]) {
    // a way across a leader costs its length and the crossing: no use looking for one when the
    // best way round is already as good as any could be
    if (soft.length === 0 && lines.length && best && best.cost <= Math.min(...plans.map(atLeast)) + crossing) break;
    const hard = [...boxes, ...soft];
    for (const [k, plan] of plans.entries()) {
      const { head, bias } = plan;
      // a way out that cannot beat the best found is not looked into; and in a crowd, when the way
      // out facing the target finds no way round, the others will not either
      if (best && atLeast(plan) >= best.cost) continue;
      if (k > 0 && !best && hard.length > 24) break;
      const from = head[head.length - 1]!;
      // the plain leader first; blocked, the shortest way round from the landing
      const plain = [...head, target];
      const checked = (pts: Vec[]): boolean => pathClear(pts.slice(0, 2), hard) && pathClear(pts.slice(1), [...hard, own]);
      let pts: Vec[] | null = null;
      if (checked(plain)) pts = plain;
      else if (pathClear(head, hard)) {
        const found = findPath(from, target, [...hard, own], bounds);
        if (found) pts = [...head.slice(0, -1), ...found];
      }
      // a way whose last turn is within the dot's ring has no room for the leader's end: not a way
      if (!pts || dist(pts[pts.length - 2]!, target) <= o.tipInset + 0.5) continue;
      // the way out that faces the target best is preferred; crossing a leader costs dear, and so does
      // one that turns back on itself or comes back past the edge of its label it left by
      const cost = length(pts) + bias + (soft.length === 0 && lines.length && !pathClear(pts, lines) ? crossing : 0) + (turnsBack(pts, plan.out, o.landing) ? 3 * crossing : 0);
      if (!best || cost < best.cost) best = { pts, cost };
    }
  }
  const chosen = best as { pts: Vec[]; cost: number } | null;
  const done = chosen ? finish(chosen.pts) : null;
  if (done) {
    done.radii = easedRadii(done.pts, o.ease, o.finish, [...boxes, own]);
    return done;
  }
  // no way at all: the plain leader, and it says so
  const first = plans[0]!.head;
  const plain = finish([...first, target]) ?? { pts: [first[0]!, target], tip: target, dir: unit(first[0]!, target), radii: [], clear: false, target };
  plain.radii = plain.pts.map(() => 0);
  plain.clear = false;
  return plain;
}

/** May a label rest here: wholly on the plan (a hair in from its edge), and off every box and dot in `avoid` (by a hair). */
export function labelRests(rect: Rect, bounds: Rect, avoid: readonly Rect[], hair: number): boolean {
  if (rect.x < bounds.x + hair || rect.y < bounds.y + hair || rect.x + rect.w > bounds.x + bounds.w - hair || rect.y + rect.h > bounds.y + bounds.h - hair) return false;
  const r = { x: rect.x - hair, y: rect.y - hair, w: rect.w + 2 * hair, h: rect.h + 2 * hair };
  return !avoid.some((o) => rectsOverlap(r, o));
}

/**
 * A place carried towards the plan's edge: it follows freely inside, and beyond the edge it gives
 * less and less, like a rubber band (there is nothing beyond to reach). `lo`, `hi`: where it may go.
 */
export function rubberBand(v: number, lo: number, hi: number, give: number): number {
  const pull = (over: number) => (1 - 1 / ((over * 0.55) / give + 1)) * give;
  return v < lo ? lo - pull(lo - v) : v > hi ? hi + pull(v - hi) : v;
}
